import { Environment, Lightformer } from '@react-three/drei'
import { useLicht } from '../scene/store'
import { useIndeling } from './indeling'
import { daklichtRijen } from './daglicht'

/**
 * Environment map, opgebouwd uit lichtvlakken in plaats van een HDRI-bestand.
 * Zonder omgeving doet `metalness` vrijwel niets — staal leest dan als mat
 * plastic. Dit geeft echte reflecties zonder een megabyte aan HDRI te moeten
 * downloaden, wat voor een offline-first kiosk uitmaakt.
 *
 * De vlakken volgen nu de echte lichtbronnen in de hal: de daklichtstraten, de
 * rijen armaturen en de lichtband rondom. Wat in een staanderkop of in de lak
 * van een truck spiegelt, hoort iets te zijn dat er ook echt hangt — anders
 * spiegelt het staal een hal die je nergens ziet.
 */
export function Omgeving() {
  const licht = useLicht()
  const ind = useIndeling()
  const rijen = licht.daklichten ? daklichtRijen(ind) : []

  return (
    <Environment resolution={256} frames={1}>
      {/* Daklichtstraten: smalle, felle, warme stroken hoog boven. */}
      {rijen.map((r, i) => (
        <Lightformer
          key={`d${i}`}
          form="rect"
          intensity={licht.omgeving * 6 * licht.daglicht}
          color="#fff1dc"
          position={[(r.x0 + r.x1) / 2, 9.4, r.z]}
          rotation={[Math.PI / 2, 0, 0]}
          scale={[r.x1 - r.x0, 1.6, 1]}
        />
      ))}

      {/* De rijen armaturen boven de gangpaden. */}
      {Array.from({ length: ind.cfg.gangpaden }, (_, i) => (
        <Lightformer
          key={`a${i}`}
          form="rect"
          intensity={licht.omgeving * 2.6 * licht.armatuurSterkte}
          color={licht.armatuurKleur}
          position={[0, 8.5, ind.gangpadZ(i)]}
          rotation={[Math.PI / 2, 0, 0]}
          scale={[46, 0.4, 1]}
        />
      ))}

      {/* De lichtband rondom: koel daglicht, laag aan de horizon van elk stuk staal. */}
      {licht.lichtband && (
        <>
          <Lightformer form="rect" intensity={licht.omgeving * 1.8 * licht.daglicht} color="#cfdcf0"
            position={[-40, 7.7, 0]} rotation={[0, Math.PI / 2, 0]} scale={[70, 1.7, 1]} />
          <Lightformer form="rect" intensity={licht.omgeving * 1.8 * licht.daglicht} color="#cfdcf0"
            position={[40, 7.7, 0]} rotation={[0, -Math.PI / 2, 0]} scale={[70, 1.7, 1]} />
          <Lightformer form="rect" intensity={licht.omgeving * 1.4 * licht.daglicht} color="#cfdcf0"
            position={[0, 7.7, -30]} rotation={[0, 0, 0]} scale={[100, 1.7, 1]} />
          <Lightformer form="rect" intensity={licht.omgeving * 1.4 * licht.daglicht} color="#cfdcf0"
            position={[0, 7.7, 30]} rotation={[0, Math.PI, 0]} scale={[100, 1.7, 1]} />
        </>
      )}

      {/* Koele vulling van opzij, geeft de staalprofielen een randlicht. */}
      <Lightformer
        form="rect" intensity={licht.omgeving * 0.7} color="#5f7299"
        position={[-26, 4, 0]} rotation={[0, Math.PI / 2, 0]} scale={[40, 6, 1]}
      />
      <Lightformer
        form="rect" intensity={licht.omgeving * 0.5} color="#52607f"
        position={[26, 4, 0]} rotation={[0, -Math.PI / 2, 0]} scale={[40, 6, 1]}
      />

      {/* Warme merkgloed aan de kopse kant — dit is wat het rood in het staal legt. */}
      <Lightformer
        form="circle" intensity={licht.omgeving * 2.4} color={licht.accentKleur}
        position={[-30, 4, 14]} rotation={[0, Math.PI / 2, 0]} scale={[12, 12, 1]}
      />

      {/* Donkere vloerbijdrage: zonder dit wordt de onderkant van alles te licht. */}
      <Lightformer
        form="rect" intensity={licht.omgeving * 0.25} color="#0d1016"
        position={[0, -2, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[50, 50, 1]}
      />
    </Environment>
  )
}
